import { injectTheme } from "./theme.js";

/**
 * A small reusable modal confirm dialog. One instance, reused. Confirm runs `onConfirm`; Cancel (or
 * clicking the backdrop / Escape) runs `onCancel`. `danger` styles the confirm button as a warning.
 */

const STYLE_ID = "gl-confirm-style";
const CSS = `
#gl-confirm-ov {
  position: fixed; inset: 0; z-index: 80; display: none;
  align-items: center; justify-content: center;
  background: rgba(2,0,4,.62); backdrop-filter: blur(2px);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
}
#gl-confirm-ov.open { display: flex; }
#gl-confirm-card {
  width: min(420px, 86vw); padding: 22px 22px 18px;
  background: var(--gl-panel); border: 2px solid var(--gl-border); border-radius: 12px;
  color: var(--gl-text); box-shadow: 0 18px 60px rgba(0,0,0,.55);
}
#gl-confirm-card h3 { margin: 0 0 10px; font-size: 15px; letter-spacing: .04em; }
#gl-confirm-card p { margin: 0 0 18px; font-size: 13px; line-height: 1.5; color: var(--gl-muted); }
#gl-confirm-card p b { color: var(--gl-error, #e05a5a); font-weight: 700; }
#gl-confirm-btns { display: flex; gap: 10px; justify-content: flex-end; }
.gl-confirm-btn {
  padding: 9px 16px; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 700;
  border: 2px solid var(--gl-border); background: var(--gl-panel-2); color: var(--gl-text);
}
.gl-confirm-btn:hover { border-color: var(--gl-border-focus); }
.gl-confirm-btn.go { background: #2a1416; border-color: #7a3a3a; color: #ffb3b3; }
.gl-confirm-btn.go:hover { border-color: #e05a5a; color: #fff; }
`;

interface ConfirmOpts {
  title: string;
  body: string;            // may contain <b> for emphasis
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

let overlay: HTMLDivElement | null = null;
let titleEl: HTMLHeadingElement, bodyEl: HTMLParagraphElement, goBtn: HTMLButtonElement, cancelBtn: HTMLButtonElement;
let current: ConfirmOpts | null = null;

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div"); overlay.id = "gl-confirm-ov";
  overlay.innerHTML = `<div id="gl-confirm-card">
    <h3></h3><p></p>
    <div id="gl-confirm-btns">
      <button class="gl-confirm-btn cancel"></button>
      <button class="gl-confirm-btn go"></button>
    </div></div>`;
  document.body.appendChild(overlay);
  titleEl = overlay.querySelector("h3")!;
  bodyEl = overlay.querySelector("p")!;
  cancelBtn = overlay.querySelector(".cancel")!;
  goBtn = overlay.querySelector(".go")!;
  cancelBtn.addEventListener("click", () => close(false));
  goBtn.addEventListener("click", () => close(true));
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(false); }); // backdrop = cancel
}

function close(confirmed: boolean): void {
  if (!overlay || !current) return;
  const opts = current; current = null;
  overlay.classList.remove("open");
  if (confirmed) opts.onConfirm(); else opts.onCancel?.();
}

/** Open the confirm dialog. Replaces any dialog already showing. */
export function showConfirm(opts: ConfirmOpts): void {
  ensure();
  current = opts;
  titleEl.textContent = opts.title;
  bodyEl.innerHTML = opts.body;
  goBtn.textContent = opts.confirmLabel ?? "Confirm";
  goBtn.classList.toggle("go", opts.danger !== false);
  cancelBtn.textContent = opts.cancelLabel ?? "Cancel";
  overlay!.classList.add("open");
}

/** Whether the confirm dialog is currently open (so callers can suppress other input). */
export function isConfirmOpen(): boolean { return !!current; }
